/**
 * claude-hook命令 - 管理Claude AI的Hook系统
 */

import { Command } from 'commander';
import { logger } from '../utils/logger.js';
import { hookSystem } from '../claude/hook-system.js';
import pc from 'picocolors';
import Table from 'cli-table3';

export function createClaudeHookCommand(): Command {
  const command = new Command('claude-hook')
    .aliases(['hook'])
    .description('管理Claude AI的Hook系统')
    .addHelpText('after', `
示例:
  workflow claude-hook list          列出所有Hook
  workflow claude-hook enable <name> 启用Hook
  workflow claude-hook test <name>   测试Hook
  workflow hook                      快捷方式：列出Hook
`);

  // 默认动作：列出Hook
  command.action(async () => {
    await listHooks();
  });

  // 列出所有Hook
  command
    .command('list')
    .aliases(['ls', 'l'])
    .description('列出所有Claude Hook')
    .action(async () => {
      await listHooks();
    });

  // 启用Hook
  command
    .command('enable <name>')
    .description('启用指定的Hook')
    .action(async (name) => {
      try {
        await hookSystem.initialize();
        await hookSystem.setHookEnabled(name, true);
        logger.success(`✅ Hook "${name}" 已启用`);
      } catch (error: any) {
        logger.error('启用Hook失败:', error.message);
        process.exit(1);
      }
    });

  // 禁用Hook
  command
    .command('disable <name>')
    .description('禁用指定的Hook')
    .action(async (name) => {
      try {
        await hookSystem.initialize();
        await hookSystem.setHookEnabled(name, false);
        logger.success(`🚫 Hook "${name}" 已禁用`);
      } catch (error: any) {
        logger.error('禁用Hook失败:', error.message);
        process.exit(1);
      }
    });

  // 测试Hook
  command
    .command('test <name>')
    .description('测试指定的Hook')
    .option('-d, --data <json>', '传递给Hook的测试数据（JSON格式）')
    .action(async (name, options) => {
      try {
        await hookSystem.initialize();
        
        logger.info(`🧪 测试Hook: ${name}`);
        
        if (options.data) {
          try {
            const data = JSON.parse(options.data);
            await hookSystem.executeHook(name, {
              type: 'test',
              timestamp: new Date(),
              data
            });
          } catch (e) {
            logger.error('JSON数据格式错误');
            process.exit(1);
          }
        } else {
          await hookSystem.testHook(name);
        }
        
        logger.success('✅ Hook测试完成');
      } catch (error: any) {
        logger.error('测试Hook失败:', error.message);
        process.exit(1);
      }
    });

  // 初始化Hook系统
  command
    .command('init')
    .description('初始化Claude Hook系统')
    .option('-f, --force', '强制重新初始化')
    .action(async (options) => {
      try {
        logger.info('🔧 初始化Claude Hook系统...');
        
        await hookSystem.initialize();
        
        logger.success('✅ Hook系统初始化成功！');
        console.log('');
        console.log('📂 Hook文件位置: .claude/hooks/');
        console.log('⚙️  配置文件: .claude/hook-config.json');
        console.log('');
        console.log('💡 提示: 使用 "workflow hook list" 查看所有可用的Hook');
      } catch (error: any) {
        logger.error('初始化Hook系统失败:', error.message);
        process.exit(1);
      }
    });

  // 编辑Hook
  command
    .command('edit <name>')
    .description('编辑Hook脚本')
    .action(async (name) => {
      try {
        const { exec } = await import('child_process');
        const path = await import('path');
        const workflowRoot = (await import('../utils/path.js')).getWorkflowRoot();
        const hookPath = path.join(workflowRoot, '.claude/hooks', name);
        
        const editor = process.env.EDITOR || 'code';
        exec(`${editor} ${hookPath}`, (error) => {
          if (error) {
            logger.error(`无法打开编辑器: ${error.message}`);
            console.log(`Hook文件位置: ${hookPath}`);
          }
        });
      } catch (error: any) {
        logger.error('编辑Hook失败:', error.message);
        process.exit(1);
      }
    });

  // 查看Hook日志
  command
    .command('logs')
    .description('查看Hook执行日志')
    .option('-n, --lines <number>', '显示最后N行日志', '20')
    .option('-f, --follow', '实时查看日志')
    .action(async (options) => {
      try {
        const fs = await import('fs-extra');
        const path = await import('path');
        const workflowRoot = (await import('../utils/path.js')).getWorkflowRoot();
        const logPath = path.join(workflowRoot, '.claude/hooks.log');
        
        if (!(await fs.pathExists(logPath))) {
          logger.warn('还没有Hook执行日志');
          return;
        }
        
        if (options.follow) {
          // 实时查看日志
          const { spawn } = await import('child_process');
          const tail = spawn('tail', ['-f', logPath]);
          tail.stdout.pipe(process.stdout);
          tail.stderr.pipe(process.stderr);
        } else {
          // 显示最后N行
          const { exec } = await import('child_process');
          exec(`tail -n ${options.lines} ${logPath}`, (error, stdout) => {
            if (error) {
              logger.error('读取日志失败:', error.message);
            } else {
              console.log(stdout);
            }
          });
        }
      } catch (error: any) {
        logger.error('查看日志失败:', error.message);
        process.exit(1);
      }
    });

  return command;
}

// 列出所有Hook的辅助函数
async function listHooks(): Promise<void> {
  try {
    await hookSystem.initialize();
    const hooks = await hookSystem.listHooks();
    
    if (hooks.length === 0) {
      logger.warn('没有找到任何Hook');
      return;
    }
    
    // 创建表格
    const table = new Table({
      head: [
        pc.cyan('Hook名称'),
        pc.cyan('状态'),
        pc.cyan('超时(ms)'),
        pc.cyan('描述')
      ],
      colWidths: [20, 10, 10, 50]
    });
    
    // 添加Hook到表格
    for (const hook of hooks) {
      const status = hook.enabled 
        ? pc.green('✓ 启用') 
        : pc.gray('✗ 禁用');
      
      table.push([
        hook.name,
        status,
        hook.timeout || '默认',
        hook.description || '无描述'
      ]);
    }
    
    console.log('');
    console.log(pc.bold('🪝 Claude Hook列表：'));
    console.log(table.toString());
    console.log('');
    console.log(pc.dim('提示: 使用 "workflow hook enable/disable <name>" 来启用/禁用Hook'));
    console.log(pc.dim('     使用 "workflow hook test <name>" 来测试Hook'));
  } catch (error: any) {
    logger.error('列出Hook失败:', error.message);
    process.exit(1);
  }
}